import { IonCol, IonContent, IonGrid, IonPage, IonRow } from '@ionic/react';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import styles from '@components/CookiePolicyPage.module.css';
import Footer from '@components/Footer';
import Heading1 from '@components/typography/Heading1';
import Heading2 from '@components/typography/Heading2';
import Heading3 from '@components/typography/Heading3';
import Hyperlink from '@components/typography/Hyperlink';
import {
  analyticsCookies,
  Cookie,
  displayCookieConsentBanner,
  functionalCookies,
  necessaryCookies,
  paymentCookies
} from '@utils/cookieUtils';
import { imageUrl } from '@utils/imageUtils';

import 'vanilla-cookieconsent';
import 'vanilla-cookieconsent/dist/cookieconsent.css';

declare global {
  interface Window {
    CookieConsentApi: any;
  }
}

type CookieTableProps = {
  cookies: Cookie[];
};

const CookieTable = ({ cookies }: CookieTableProps) => (
  <table className={styles.cookieTable}>
    <thead>
      <tr>
        <th>Cookie</th>
        <th>Domain</th>
        <th>Vendor</th>
      </tr>
    </thead>
    <tbody>
      {cookies.map(cookie => (
        <tr key={cookie.cookie}>
          <td>{cookie.cookie}</td>
          <td>{cookie.domain}</td>
          <td>{cookie.vendor}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const CookiePolicyPage = () => {
  const intl = useIntl();

  const showCookieDeclaration = useCallback(() => {
    displayCookieConsentBanner();
  }, []);

  return (
    <IonPage className={styles.page}>
      <IonContent>
        <IonGrid fixed>
          <IonRow>
            <IonCol className={styles.logoColumn}>
              <img
                alt={intl.formatMessage({ id: 'accessibility.altTags.purpleLogo' })}
                className={styles.logo}
                src={imageUrl('logoPurple')}
              />
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol>
              <Heading1>Cookie Policy</Heading1>
              <p>
                This Cookie Policy explains what cookies are and how we use them. You should read this policy to
                understand what cookies are, how we use them, the types of cookies we use i.e, the information we
                collect using cookies and how that information is used and how to control the cookie preferences. For
                further information on how we use, store and keep your personal data secure, see our{' '}
                <Hyperlink href="https://www.withgrayce.com/privacy">Privacy Policy</Hyperlink>.
              </p>

              <p>
                You can at any time change or withdraw your consent:{' '}
                <Hyperlink onClick={showCookieDeclaration}>manage my consent.</Hyperlink>
              </p>

              <p>
                Learn more about who we are, how you can contact us and how we process personal data in our Privacy
                Policy.
              </p>

              <Heading2>What are cookies?</Heading2>

              <p>
                Cookies are small files that a web server sends to your computer or device when you visit a web site
                that uses cookies to keep track of your activity on that site. They hold a small amount of data specific
                to that web site, which can later be used to help remember information you enter into the site (like
                your email or other contact info), preferences selected, and movement within the site. If you return to
                the previously visited web site (and your browser has cookies enabled), the web browser sends the small
                file to the web server, which tells it what activity you engaged in the last time you used the site, and
                the server can use the cookie to do things like expedite logging in and retrieving user data and keeping
                your browser session secure.
              </p>

              <Heading2>How do we use cookies?</Heading2>

              <p>
                We use cookies and other technologies to, among other things, better serve you with more tailored
                information, and to facilitate efficient and secure access to the Website. Essential cookies are those
                necessary for use to provide services to you. All other cookies are non-essential. We use two types of
                non-essential cookies: (1) cookies used to analyze your behavior on a website (“Analytics Cookies”); and
                (2) cookies used to provide you enhanced functionality (“Functional cookies”). We have provided, below,
                a full list of our cookies, categorized as described above. We have described the purpose of each,
                whether they are Grayce or Third Party cookies, and how to withdraw consent to their use. We have also
                indicated which cookies are “session cookies” (which last for as long as you keep your browser open) and
                “persistent cookies” (which remain on your hard drive until you delete them or they expire).
              </p>

              <p>
                We may also collect information using pixel tags, web beacons, clear GIFs or other similar technologies.
                These may be used in connection with some Site pages and HTML formatted email messages to, among other
                things, track the actions of Site users and email recipients, and compile statistics about Site usage
                and response rates.
              </p>

              <Heading2>What types of cookies do we use?</Heading2>

              <p>The cookies used on our website are grouped into the following categories:</p>

              <Heading3>Required cookies</Heading3>
              <CookieTable cookies={necessaryCookies} />

              <Heading3>Functional cookies</Heading3>
              <CookieTable cookies={functionalCookies} />

              <Heading3>Analytics cookies</Heading3>
              <CookieTable cookies={analyticsCookies} />

              <Heading3>Payment cookies</Heading3>
              <CookieTable cookies={paymentCookies} />

              <Heading2>How can I control the cookie preferences?</Heading2>

              <p>
                You can manage your cookies preferences by making changes in the consent popup, enabling or disabling
                the cookie categories according to your preferences.
              </p>

              <p>
                Should you decide to change your preferences later through your browsing session, you can click on the
                “manage my consent” link above. This will display the consent notice again enabling you to change your
                preferences or withdraw your consent entirely.
              </p>

              <p>
                If you prefer, you can usually choose to set your browser to remove cookies and reject cookies. If you
                configure your browser to prevent Grayce from collecting cookies, You will not be able to login to
                Grayce services. Such action could also affect certain features or services of our Website.
              </p>

              <p>
                You may opt-out from the collection of non-essential device and usage data on our site by managing your
                cookies at the individual browser level.{' '}
                <Hyperlink href="https://www.allaboutcookies.org/manage-cookies/">Click here</Hyperlink> for ways to
                erase cookies from your computer and to prevent cookies from being created on your browser.
              </p>

              <p>
                Please note, however, that by blocking or deleting cookies and similar technologies used on our
                websites, You may not be able to take full advantage of the websites.
              </p>

              <p>
                Separately, your browser may offer you a “Do Not Track” option, which allows you to signal to operators
                of websites and web applications and services that you do not wish such operators to track certain of
                your online activities over time and across different websites. Our Services do not support Do Not Track
                requests at this time. To find out more about “Do Not Track,” you can visit www.allaboutdnt.com.
              </p>
            </IonCol>
          </IonRow>
        </IonGrid>
        <Footer />
      </IonContent>
    </IonPage>
  );
};

export default CookiePolicyPage;
